import isNil from 'lodash/isNil'
import type { Image, ScheduleEvent, ScheduleEventGameData, ScheduleWeek, StandingRow, Team } from '~/types'
import {
  EventPrepositionLabel,
  IN_GAME_STATUSES,
  POST_GAME_STATUSES,
  PRE_GAME_STATUSES,
  ResultLabel,
  SCHEDULED_STATUSES,
  Status,
  StatusLabel,
  TBD_STATUSES,
} from '~/enums/schedule-event'
import { Label } from '~/enums/label'

export interface TeamsData {
  home: TeamData
  away: TeamData
}

export interface TeamData {
  srId: Maybe<string>
  name: Maybe<string>
  alias: Maybe<string>
  logo: Maybe<Image>
  score: Maybe<number>
  recordsString: Maybe<string>
  isWin: boolean
  color: Maybe<string>
}

export const useScheduleEvent = (eventRef: MaybeRefOrGetter<Maybe<ScheduleEvent.Model | undefined>> | undefined, team?: MaybeRef<Team.Model>) => {
  const event = ref<Maybe<ScheduleEvent.Model>>()
  const { timezone: teamTimezone } = useTeam(team)

  const { getStreamById } = useLiveGamesStore()

  const srId = computed<Maybe<string>>(() => unref(event)?.srId || null)

  const {
    homePoints: boxScoreHomePoints,
    awayPoints: boxScoreAwayPoints,
    homeTeamRecords: boxScoreHomeRecords,
    awayTeamRecords: boxScoreAwayRecords,
  } = useBoxScore(srId)

  const id = computed<Maybe<number>>(() => unref(event)?.id || null)
  const stream = computed<Maybe<ScheduleEventGameData.Model>>(() => getStreamById(srId.value))
  const rawStatus = computed<Maybe<ScheduleEvent.Status>>(() => unref(event)?.status || null)
  const datetime = computed<Maybe<string>>(() => unref(event)?.datetime || null)
  const timezone = computed<string>(() => teamTimezone.value || unref(event)?.timezone || 'UTC')
  const gameType = computed<Maybe<string>>(() => unref(event)?.gameType || null)
  const conferenceGame = computed<Maybe<boolean>>(() => unref(event)?.conferenceGame || null)
  const duration = computed<Maybe<string>>(() => unref(event)?.duration || null)
  const venueName = computed<Maybe<string>>(() => unref(event)?.venueName || null)
  const venueAddress = computed<Maybe<string>>(() => unref(event)?.venueAddress || null)
  const venue = computed<Maybe<ScheduleEvent.Venue>>(() => unref(event)?.venue || null)
  const status = computed<Maybe<ScheduleEvent.Status>>(() => {
    let currentStatus = rawStatus.value

    if (stream.value && stream.value?.game?.status) {
      currentStatus = stream.value.game.status
    }

    return currentStatus
  })
  const isPreGame = computed<boolean>(() => Boolean(status.value && PRE_GAME_STATUSES.includes(status.value)))
  const isLive = computed<boolean>(() => Boolean(status.value && IN_GAME_STATUSES.includes(status.value)))
  const isFinished = computed<boolean>(() => Boolean(status.value && POST_GAME_STATUSES.includes(status.value)))
  const location = computed<Maybe<string>>(() => {
    if (!venue.value?.address) {
      return null
    }
    const { city, state } = venue.value.address

    if (!city || !state) {
      return null
    }

    return `${city}, ${state}`
  })
  const homeGameNumber = computed<Maybe<number>>(() => unref(event)?.homeGameNumber || null)
  const awayGameNumber = computed<Maybe<number>>(() => unref(event)?.awayGameNumber || null)
  const homeTeamScore = computed<Maybe<number>>(() => !isPreGame.value ? boxScoreHomePoints.value ?? unref(event)?.homeTeamScore ?? null : null)
  const awayTeamScore = computed<Maybe<number>>(() => !isPreGame.value ? boxScoreAwayPoints.value ?? unref(event)?.awayTeamScore ?? null : null)

  const resolveRowByCategory = (category: string, rows: StandingRow.Record[]): StandingRow.Record | undefined => {
    return rows.find(row => row.category === category)
  }

  const resolveRecordStringFromStandingRows = (rows: StandingRow.Record[]): Maybe<string> => {
    const parts: string[] = []

    const conferenceRow = resolveRowByCategory('conference', rows)

    if (!conferenceRow) {
      return null
    }

    parts.push(`${conferenceRow.wins} - ${conferenceRow.losses}`)

    const divisionRow = resolveRowByCategory('division', rows)

    if (divisionRow) {
      parts.push(`${divisionRow.wins} - ${divisionRow.losses}`)
    }

    return parts.join(', ')
  }

  const homeTeamRecord = computed<StandingRow.Record[]>(() => unref(event)?.homeTeamRecord ?? [])
  const homeTeamRecordText = computed<Maybe<string>>(() => {
    if (!Array.isArray(homeTeamRecord.value) || homeTeamRecord.value?.length === 0) {
      return null
    }

    return resolveRecordStringFromStandingRows(homeTeamRecord.value)
  })
  const awayTeamRecord = computed<StandingRow.Record[]>(() => unref(event)?.awayTeamRecord ?? [])
  const awayTeamRecordText = computed<Maybe<string>>(() => {
    if (!Array.isArray(homeTeamRecord.value) || awayTeamRecord.value?.length === 0) {
      return null
    }

    return resolveRecordStringFromStandingRows(awayTeamRecord.value)
  })
  const homeTeam = computed<Maybe<Team.Model>>(() => unref(event)?.homeTeam || null)
  const awayTeam = computed<Maybe<Team.Model>>(() => unref(event)?.awayTeam || null)
  const homeTeamLogo = computed<Maybe<Image>>(() => unref(event)?.homeTeam?.logo || null)
  const awayTeamLogo = computed<Maybe<Image>>(() => unref(event)?.awayTeam?.logo || null)
  const homeTeamName = computed<string>(() => unref(event)?.homeTeamName || Label.TBA)
  const awayTeamName = computed<string>(() => unref(event)?.awayTeamName || Label.TBA)
  const homeTeamAlias = computed<string>(() => unref(event)?.homeTeamAlias || Label.TBA)
  const awayTeamAlias = computed<string>(() => unref(event)?.awayTeamAlias || Label.TBA)
  const homeTeamSrId = computed<Maybe<string>>(() => homeTeam.value?.srId ?? null)
  const awayTeamSrId = computed<Maybe<string>>(() => awayTeam.value?.srId ?? null)
  const scheduleEventLinks = computed<ScheduleEvent.Link[]>(() => unref(event)?.scheduleEventLinks || [])
  const title = computed<Maybe<string>>(() => `${homeTeamName.value} ${vsAtData.value} ${awayTeamName.value}`)
  const weatherText = computed<Maybe<string>>(() => {
    const { weather } = unref(event) || {}
    if (!weather) {
      return null
    }

    const weatherDetails = [
      weather.condition ? `${weather.condition}` : null,
      weather.temp ? `${weather.temp}°F` : null,
      weather.humidity ? `Humidity: ${weather.humidity}%` : null,
      weather.windSpeed ? `Wind: ${weather.windSpeed} mph` : null,
      weather.windDirection ? `${weather.windDirection}` : null,
    ]
      .filter(Boolean)
      .join(', ')

    return weatherDetails || null
  })

  const officials = computed<ScheduleEvent.Official[]>(() => unref(event)?.officials || [])

  const opponentTeam = computed<Maybe<Team.Model>>(() => {
    if (homeTeam.value?.id === unref(team)?.id) {
      return awayTeam.value || null
    }
    // In case if the team passed to the method is completely wrong
    else if (awayTeam.value?.id === unref(team)?.id) {
      return homeTeam.value || null
    }
    return null
  })

  const isHomeGame = computed<boolean>(() => {
    return homeTeam.value?.id === unref(team)?.id
  })
  const isAwayGame = computed<boolean>(() => !isHomeGame.value)

  const scheduleEventScheduleViewLinksRaw = computed<ScheduleEvent.Link[]>(() => scheduleEventLinks.value.filter(item => item.displayOnScheduleView))
  const scheduleEventLeagueViewLinksRaw = computed<ScheduleEvent.Link[]>(() => scheduleEventLinks.value.filter(item => item.displayOnLeagueScheduleView))

  const {
    filteredLinks: scheduleEventScheduleViewLinks,
    hasLinks: hasScheduleEventScheduleViewLinks,
  } = useScheduleEventLinks(status, scheduleEventScheduleViewLinksRaw, team)
  const {
    filteredLinks: scheduleEventLeagueViewLinks,
    hasLinks: hasScheduleEventLeagueViewLinks,
  } = useScheduleEventLinks(status, scheduleEventLeagueViewLinksRaw, team)

  const isAwayTeamWin = computed<boolean>(() => {
    if (isNil(homeTeamScore.value) || isNil(awayTeamScore.value)) {
      return false
    }
    return awayTeamScore.value > homeTeamScore.value
  })
  const isHomeTeamWin = computed<boolean>(() => {
    if (isNil(homeTeamScore.value) || isNil(awayTeamScore.value)) {
      return false
    }
    return awayTeamScore.value < homeTeamScore.value
  })

  const teamsData = computed<TeamsData>(() => ({
    home: homeTeamData.value,
    away: awayTeamData.value,
  }))

  const homeTeamData = computed<TeamData>(() => ({
    srId: homeTeamSrId.value,
    logo: homeTeamLogo.value,
    name: homeTeamName.value,
    alias: homeTeamAlias.value,
    score: homeTeamScore.value,
    recordsString: homeTeamRecordText.value ?? boxScoreHomeRecords.value,
    isWin: isHomeTeamWin.value,
    color: homeTeam.value?.color || null,
  }))

  const awayTeamData = computed<TeamData>(() => ({
    srId: awayTeamSrId.value,
    logo: awayTeamLogo.value,
    name: awayTeamName.value,
    alias: awayTeamAlias.value,
    score: awayTeamScore.value,
    recordsString: awayTeamRecordText.value ?? boxScoreAwayRecords.value,
    isWin: isAwayTeamWin.value,
    color: awayTeam.value?.color || null,
  }))

  const { dateLuxonToFormat: eventDateFormatted } = useDate(datetime, undefined, timezone.value)
  const { dateLuxonToFormat: time } = useDate(datetime, 't ZZZZ', timezone.value)
  const { dateLuxonToFormat: eventDateTimeFormatted } = useDate(datetime, 'MMMM dd, yyyy, t ZZZZ', timezone.value)

  const week = computed<Maybe<ScheduleWeek.Model>>(() => unref(event)?.scheduleWeek || null)
  const broadcast = computed<ScheduleEvent.Broadcast[]>(() => unref(event)?.broadcast || [])
  const scheduleEventBroadcasts = computed<ScheduleEvent.ScheduleEventBroadcast[]>(() => unref(event)?.scheduleEventBroadcasts || [])
  const hasScheduleEventBroadcast = computed<boolean>(() => scheduleEventBroadcasts.value.length > 0)

  const isStatusScheduled = computed<boolean>(() => null !== status.value && SCHEDULED_STATUSES.includes(status.value))
  const isStatusClosed = computed<boolean>(() => status.value === Status.Closed)
  const isStatusCompleted = computed<boolean>(() => status.value === Status.Complete)
  const isStatusPostponed = computed<boolean>(() => status.value === Status.Postponed)
  const isTbd = computed<boolean>(() => null !== status.value && TBD_STATUSES.includes(status.value))

  const statusText = computed<Maybe<string>>(() => {
    if (isTbd.value) {
      return StatusLabel.TimeTbd
    }
    if (isStatusClosed.value || isStatusCompleted.value) {
      return StatusLabel.Complete
    }
    if (isStatusPostponed.value) {
      return StatusLabel.Postponed
    }

    return null
  })

  const sponsors = computed<ScheduleEvent.ScheduleEventSponsor[]>(() => unref(event)?.scheduleEventSponsors || [])
  const hasSponsors = computed<boolean>(() => sponsors.value.length > 0)
  const sponsor = computed<Maybe<ScheduleEvent.ScheduleEventSponsor>>(() => {
    if (!hasSponsors.value) {
      return null
    }
    const teamId = unref(team)?.id ?? null

    return sponsors.value.find(sponsor => sponsor.teamId === teamId) ?? null
  })

  const isCurrentTeamAwayTeam = computed<boolean>(() => awayTeam.value?.id ? unref(team)?.id === awayTeam.value.id : false)

  const currentTeamData = computed<TeamData>(() => {
    if (isCurrentTeamAwayTeam.value) {
      return awayTeamData.value
    }
    return homeTeamData.value
  })

  const currentTeamInfo = computed<Maybe<Team.Model>>(() => {
    if (isCurrentTeamAwayTeam.value) {
      return awayTeam.value
    }
    return homeTeam.value
  })

  const opponentTeamInfo = computed<Maybe<Team.Model>>(() => {
    if (!isCurrentTeamAwayTeam.value) {
      return awayTeam.value
    }
    return homeTeam.value
  })

  const opponentTeamData = computed<TeamData>(() => {
    if (isCurrentTeamAwayTeam.value) {
      return awayTeamData.value
    }
    return homeTeamData.value
  })

  const opponentTeamScore = computed<Maybe<number>>(() => {
    if (!isCurrentTeamAwayTeam.value) {
      return awayTeamScore.value
    }
    return homeTeamScore.value
  })

  const currentTeamScore = computed<Maybe<number>>(() => {
    if (isCurrentTeamAwayTeam.value) {
      return awayTeamScore.value
    }
    return homeTeamScore.value
  })

  const resultLabel = computed<Maybe<string>>(() => {
    if (isNil(currentTeamScore.value) || isNil(opponentTeamScore.value)) {
      return null
    }
    if (currentTeamScore.value > opponentTeamScore.value) {
      return ResultLabel.Win
    }

    if (currentTeamScore.value === opponentTeamScore.value) {
      return ResultLabel.Tie
    }

    return ResultLabel.Loss
  })

  const vsAtData = computed<string>(() => {
    if (isCurrentTeamAwayTeam.value) {
      return EventPrepositionLabel.at
    }
    return EventPrepositionLabel.vs
  })

  const hasResults = computed<boolean>(() => isFinished.value && !isNil(currentTeamScore.value) && !isNil(opponentTeamScore.value) && !isStatusScheduled.value)

  const teamLeaders = computed<ScheduleEvent.TeamLeader[]>(() => unref(event)?.scheduleEventTeamLeaders || [])
  const hasTeamLeaders = computed<boolean>(() => teamLeaders.value.length > 0)

  watchEffect(() => {
    event.value = toValue(eventRef)
  })

  return {
    awayGameNumber,
    awayTeam,
    awayTeamData,
    awayTeamLogo,
    awayTeamName,
    awayTeamScore,
    broadcast,
    conferenceGame,
    currentTeamData,
    currentTeamInfo,
    currentTeamScore,
    datetime,
    duration,
    eventDateFormatted,
    eventDateTimeFormatted,
    gameType,
    hasResults,
    hasScheduleEventBroadcast,
    hasScheduleEventLeagueViewLinks,
    hasScheduleEventScheduleViewLinks,
    hasTeamLeaders,
    homeGameNumber,
    homeTeam,
    homeTeamData,
    homeTeamLogo,
    homeTeamName,
    homeTeamScore,
    id,
    isAwayGame,
    isAwayTeamWin,
    isCurrentTeamAwayTeam,
    isHomeGame,
    isHomeTeamWin,
    isLive,
    isStatusClosed,
    isStatusPostponed,
    isStatusScheduled,
    isTbd,
    location,
    opponentTeam,
    opponentTeamData,
    opponentTeamInfo,
    opponentTeamScore,
    resultLabel,
    scheduleEventBroadcasts,
    scheduleEventLeagueViewLinks,
    scheduleEventLinks,
    scheduleEventScheduleViewLinks,
    sponsor,
    srId,
    status,
    statusText,
    teamLeaders,
    time,
    timezone,
    venue,
    venueAddress,
    venueName,
    vsAtData,
    week,
    title,
    homeTeamAlias,
    awayTeamAlias,
    weatherText,
    isFinished,
    isPreGame,
    officials,
    teamsData,
    homeTeamSrId,
    awayTeamSrId,
  }
}
